import { Button, Modal, cn } from "@vidario/ui";
import { AlertTriangle } from "lucide-react";
import { type ReactElement } from "react";

import { useAnonymView } from "../../anonym/useAnonymView";

export interface ReleaseRecommendationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  loading?: boolean;
  /** The job's recruiter, who will see the recommendation and get the email.
   * Omitted while the user list is still loading. */
  recruiter?: { name: string; email: string } | undefined;
  /** Recommended candidates without a recommendation text. */
  missingText: readonly { id: string; name: string; code?: string | undefined }[];
}

/** Confirm before the recommendation release (ADR 0004). The release can't
 * be undone and emails the customer, so a stray click must not be enough. */
export function ReleaseRecommendationDialog({
  open,
  onOpenChange,
  onConfirm,
  loading = false,
  recruiter,
  missingText,
}: ReleaseRecommendationDialogProps): ReactElement {
  const anonym = useAnonymView();
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Empfehlung freigeben?"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button
            loading={loading}
            onClick={onConfirm}
            data-testid="confirm-release-recommendation"
          >
            {missingText.length > 0 ? "Trotzdem freigeben" : "Freigeben"}
          </Button>
        </>
      }
    >
      <p className="text-sm text-text-primary">
        {recruiter ? (
          <>
            Die Empfehlung wird für <b>{recruiter.name}</b> sichtbar, und {recruiter.email} erhält
            eine E-Mail.
          </>
        ) : (
          "Die Empfehlung wird für den Recruiter sichtbar, und er erhält eine E-Mail."
        )}{" "}
        Das lässt sich nicht rückgängig machen.
      </p>
      {missingText.length > 0 && (
        <div
          className="mt-3 flex gap-2 rounded-md bg-warning-50 p-3 text-sm text-warning-700"
          role="status"
        >
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">Noch kein Empfehlungstext für:</div>
            <ul className="mt-1 list-disc pl-4 text-xs">
              {missingText.map((c) => (
                <li key={c.id} className={cn(anonym && "font-mono")}>
                  {anonym ? (c.code ?? "Kandidat") : c.name}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </Modal>
  );
}
